import { useEffect, useRef, useState } from 'react';
import {
	Box,
	Collapse,
	Divider,
	Drawer,
	IconButton,
	Stack,
	Tooltip,
	Typography,
	useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useLocation } from 'react-router-dom';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import KeyboardDoubleArrowLeftRoundedIcon from '@mui/icons-material/KeyboardDoubleArrowLeftRounded';
import KeyboardDoubleArrowRightRoundedIcon from '@mui/icons-material/KeyboardDoubleArrowRightRounded';
import { menuSections, MenuItem, MenuSection } from '../../config';
import { isPathActive, SideNavItem } from './side-nav-item';
import { useAuthContext, isRootAdmin, isShopAdmin } from '../../contexts/auth-context';
import { useShopDetails } from '../../hooks/use-shop';
import { StaffPerLine } from '../../utils/business-profile';
import { Scrollbar } from './scrollbar';
import {
	SIDE_NAV_WIDTH,
	SIDE_NAV_WIDTH_COLLAPSED,
	SIDE_NAV_WIDTH_TABLET,
	useNavTransition,
} from './layout-constants';

interface SideNavProps {
	/** Mobile drawer visibility (below `md`). */
	open: boolean;
	onClose: () => void;
	/** Desktop rail mode (`md` and up). */
	collapsed: boolean;
	onToggleCollapse: () => void;
}

const NAV_ID = 'app-side-nav';

export const SideNav = ({ open, onClose, collapsed, onToggleCollapse }: SideNavProps) => {
	const theme = useTheme();
	const lgUp = useMediaQuery(theme.breakpoints.up('lg'));
	const mdUp = useMediaQuery(theme.breakpoints.up('md'));
	const { pathname } = useLocation();
	const { shopDetails, jwtDecoded } = useAuthContext();
	const { shopName: cachedShopName, profile } = useShopDetails();
	const transition = useNavTransition(['width']);
	/* Sections start closed; only the one holding the current page is open. */
	const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>(() => {
		const owner = menuSections.find((section) => section.items.some((item) => isPathActive(pathname, item.path)));
		return owner ? { [owner.sectionTitle]: true } : {};
	});

	/* The rail only exists on desktop; the mobile drawer always shows full labels. */
	const isRail = mdUp && collapsed;
	const expandedWidth = lgUp ? SIDE_NAV_WIDTH : SIDE_NAV_WIDTH_TABLET;
	const width = mdUp ? (isRail ? SIDE_NAV_WIDTH_COLLAPSED : expandedWidth) : SIDE_NAV_WIDTH;

	const hasAccessToItem = (item: MenuItem): boolean => {
		// An entry gated on a profile trait is hidden where the location lacks
		// it. The routes redirect too (see Routes.tsx) — hiding the entry alone
		// would leave the pages reachable by URL or an old bookmark.
		if (item.requires === 'services' && !profile.servicesEnabled) return false;
		if (item.requires === 'staff' && profile.staffPerLine === StaffPerLine.Off) return false;
		if (!item.roles || item.roles.length === 0) return true;
		if (item.roles.includes('SHOP_ADMIN') || item.roles.includes('SUPER_ADMIN')) {
			return isShopAdmin(jwtDecoded) || isRootAdmin(jwtDecoded);
		}
		return true;
	};

	/* Entries gated on a trait take their title from the location's labels. */
	const titled = (item: MenuItem): MenuItem => {
		if (item.requires === 'services') return { ...item, title: profile.servicesLabel };
		if (item.requires === 'staff') return { ...item, title: `${profile.staffLabel} Commission` };
		return item;
	};
	const sections = menuSections
		.map((section: MenuSection) => ({ ...section, items: section.items.filter(hasAccessToItem).map(titled) }))
		.filter((section) => section.items.length > 0);

	/*
	 * When navigation lands in a different section, that section opens itself. Moving
	 * within the same section (list → edit) leaves a manual collapse alone.
	 */
	const previousOwner = useRef<string | undefined>(undefined);
	useEffect(() => {
		const owner = sections.find((section) => section.items.some((item) => isPathActive(pathname, item.path)));
		const title = owner?.sectionTitle;
		if (title && title !== previousOwner.current && expandedSections[title] !== true) {
			setExpandedSections((previous) => ({ ...previous, [title]: true }));
		}
		previousOwner.current = title;
	}, [pathname]);

	const toggleSection = (sectionTitle: string) =>
		setExpandedSections((previous) => ({ ...previous, [sectionTitle]: previous[sectionTitle] !== true }));

	/* The query result wins; the cached name from login fills the gap while it loads. */
	const shopName = shopDetails?.name || cachedShopName || '';
	const shopInitial = shopName.trim().charAt(0).toUpperCase() || '·';

	const header = (
		<Box sx={{ px: isRail ? 1.5 : 3, pt: 3, pb: 2.5 }}>
			<Tooltip title={isRail ? shopName : ''} placement="right" arrow>
				<Box
					sx={{
						alignItems: 'center',
						backgroundColor: 'rgba(255, 255, 255, 0.04)',
						borderRadius: 1,
						display: 'flex',
						justifyContent: isRail ? 'center' : 'space-between',
						minHeight: 48,
						px: isRail ? 0 : 1.5,
						overflow: 'hidden',
					}}
				>
					{isRail ? (
						<Typography variant="subtitle1" sx={{ fontWeight: 700, color: 'common.white' }} aria-label={shopName}>
							{shopInitial}
						</Typography>
					) : (
						<Typography
							color="inherit"
							variant="subtitle1"
							sx={{ fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
						>
							{shopName}
						</Typography>
					)}
				</Box>
			</Tooltip>
		</Box>
	);

	const nav = (
		<Box component="nav" id={NAV_ID} aria-label="Main navigation" sx={{ flexGrow: 1, px: 1.5, py: 2 }}>
			<Stack component="ul" spacing={0.5} sx={{ listStyle: 'none', p: 0, m: 0 }}>
				{sections.map((section, index) => {
					const isExpanded = expandedSections[section.sectionTitle] === true;
					const items = section.items.map((item) => (
						<SideNavItem
							key={item.path}
							collapsed={isRail}
							icon={item.icon}
							path={item.path}
							title={item.title}
							onNavigate={mdUp ? undefined : onClose}
						/>
					));

					if (isRail) {
						/* Rail: section headers become hairline separators; every item stays reachable. */
						return (
							<Box component="li" key={section.sectionTitle} sx={{ listStyle: 'none' }}>
								{index > 0 && <Divider sx={{ borderColor: 'neutral.700', my: 1, mx: 1 }} />}
								<Stack component="ul" spacing={0.5} sx={{ listStyle: 'none', p: 0, m: 0 }} aria-label={section.sectionTitle}>
									{items}
								</Stack>
							</Box>
						);
					}

					return (
						<Box component="li" key={section.sectionTitle} sx={{ listStyle: 'none', mb: 1 }}>
							<Box
								component="button"
								type="button"
								aria-expanded={isExpanded}
								onClick={() => toggleSection(section.sectionTitle)}
								sx={{
									all: 'unset',
									boxSizing: 'border-box',
									cursor: 'pointer',
									display: 'flex',
									alignItems: 'center',
									justifyContent: 'space-between',
									width: '100%',
									px: 1.5,
									py: 1,
									borderRadius: 1,
									color: 'neutral.400',
									'&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.04)' },
									'&:focus-visible': { outline: '2px solid', outlineColor: 'primary.main', outlineOffset: -2 },
								}}
							>
								<Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0, overflow: 'hidden', whiteSpace: 'nowrap' }}>
									{section.sectionIcon}
									<Typography
										component="span"
										variant="subtitle2"
										sx={{ color: 'neutral.400', fontWeight: 600, textTransform: 'uppercase', fontSize: '0.75rem', letterSpacing: '0.04em' }}
									>
										{section.sectionTitle}
									</Typography>
								</Box>
								{isExpanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
							</Box>

							<Collapse in={isExpanded} timeout="auto" unmountOnExit>
								<Stack component="ul" spacing={0.5} sx={{ listStyle: 'none', p: 0, m: 0, mt: 0.5 }}>
									{items}
								</Stack>
							</Collapse>
						</Box>
					);
				})}
			</Stack>
		</Box>
	);

	/* Desktop only: a second, in-place affordance to collapse — the top-bar button is the primary one. */
	const footer = mdUp && (
		<Box sx={{ p: 1.5, borderTop: '1px solid', borderColor: 'neutral.700', display: 'flex', justifyContent: isRail ? 'center' : 'flex-end' }}>
			<Tooltip title={isRail ? 'Expand menu' : 'Collapse menu'} placement="right" arrow>
				<IconButton
					size="small"
					onClick={onToggleCollapse}
					aria-label={isRail ? 'Expand menu' : 'Collapse menu'}
					aria-expanded={!isRail}
					aria-controls={NAV_ID}
					sx={{ color: 'neutral.400', '&:hover': { color: 'common.white', backgroundColor: 'rgba(255, 255, 255, 0.06)' } }}
				>
					{isRail ? <KeyboardDoubleArrowRightRoundedIcon fontSize="small" /> : <KeyboardDoubleArrowLeftRoundedIcon fontSize="small" />}
				</IconButton>
			</Tooltip>
		</Box>
	);

	const content = (
		<Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', width, transition, overflow: 'hidden', whiteSpace: 'nowrap' }}>
			{header}
			<Divider sx={{ borderColor: 'neutral.700' }} />
			<Scrollbar sx={{ flexGrow: 1, minHeight: 0, '& .simplebar-content': { height: '100%' } }}>{nav}</Scrollbar>
			{footer}
		</Box>
	);

	const paperSx = {
		backgroundColor: 'neutral.800',
		color: 'common.white',
		width,
		overflowX: 'hidden',
		borderRight: 'none',
		transition,
	};

	if (mdUp) {
		return (
			<Drawer anchor="left" open variant="permanent" PaperProps={{ sx: paperSx }}>
				{content}
			</Drawer>
		);
	}

	return (
		<Drawer
			anchor="left"
			onClose={onClose}
			open={open}
			variant="temporary"
			ModalProps={{ keepMounted: true }}
			PaperProps={{ sx: paperSx }}
			sx={{ zIndex: theme.zIndex.appBar + 100 }}
		>
			{content}
		</Drawer>
	);
};
